/**
 * CompactCardsSlider — carousel custom element for compact card groups.
 *
 * Shows one card at a time via translateX sliding with infinite looping.
 * Activates when the number of cards exceeds data-threshold (default 1):
 * one card = static, two or more = slider.
 *
 * Features: autoplay, prev/next arrows, dot indicators, infinite loop,
 * pause on hover/focus, respects prefers-reduced-motion, full ARIA.
 *
 * Progressive enhancement: without JS all cards render stacked (existing layout).
 */

class CompactCardsSlider extends HTMLElement {
  /** Logical index into real slides (0-based). */
  #current = 0;

  /** @type {boolean} */
  #sliderActive = false;

  /** @type {number | undefined} */
  #autoplayTimer;

  /** @type {boolean} */
  #paused = false;

  /** @type {boolean} */
  #transitioning = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number} Number of real (non-clone) slides. */
  #realCount = 0;

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    this.#evaluate();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  // ─── DOM refs ──────────────────────────────────────────────────────────────

  get #cardCount() {
    return parseInt(this.dataset.cardCount ?? "0", 10);
  }

  get #autoplaySeconds() {
    return parseInt(this.dataset.autoplay ?? "5", 10);
  }

  get #threshold() {
    return parseInt(this.dataset.threshold ?? "1", 10);
  }

  get #shouldActivate() {
    return this.#cardCount > this.#threshold;
  }

  get #trackWrapper() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-track-wrapper]"));
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-track]"));
  }

  get #controls() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-controls]"));
  }

  get #prevBtn() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-prev]"));
  }

  get #nextBtn() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-next]"));
  }

  get #liveRegion() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-live-region]"));
  }

  get #dots() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll("[data-dot]")));
  }

  /**
   * All slides in the track, including clones. Only `[data-slide]` children count:
   * product cards render their quick-shop dialog as a sibling, which must not be a slide.
   */
  get #allSlides() {
    return /** @type {HTMLElement[]} */ (
      this.#track ? Array.from(this.#track.querySelectorAll(":scope > [data-slide]")) : []
    );
  }

  get #reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  get #slideWidth() {
    return this.#trackWrapper?.offsetWidth ?? 0;
  }

  // ─── Evaluation & activation ───────────────────────────────────────────────

  #evaluate() {
    if (this.#shouldActivate) {
      this.#activate();
    } else {
      this.#deactivate();
    }
  }

  #activate() {
    if (this.#sliderActive) return;

    this.#sliderActive = true;
    this.setAttribute("slider-active", "");

    const controls = this.#controls;
    if (!controls) return;

    this.#realCount = this.#cardCount;

    const track = this.#track;
    if (track) track.style.display = "flex";

    // ── ARIA on real slides ─────────────────────────────────────────────
    const slides = this.#allSlides;
    slides.forEach((slide, i) => {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "item");
      slide.setAttribute("aria-label", this.#slideLabel(i + 1, slides.length));
      slide.style.flexShrink = "0";
    });

    // ── Create bookend clones for infinite loop ─────────────────────────
    this.#createClones();
    this.#sizeSlides();

    // ── Resize observer ─────────────────────────────────────────────────
    const wrapper = this.#trackWrapper;
    if (wrapper) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#sizeSlides();
        this.#jumpTo(this.#current);
      });
      this.#resizeObserver.observe(wrapper);
    }

    // ── Show controls ───────────────────────────────────────────────────
    controls.classList.remove("hidden");
    controls.classList.add("block");
    controls.removeAttribute("aria-hidden");

    // ── Event listeners ─────────────────────────────────────────────────
    this.#prevBtn?.addEventListener("click", this.#handlePrev);
    this.#nextBtn?.addEventListener("click", this.#handleNext);

    this.#dots.forEach((dot) => {
      dot.addEventListener("click", this.#handleDotClick);
    });

    this.addEventListener("mouseenter", this.#handleMouseEnter);
    this.addEventListener("mouseleave", this.#handleMouseLeave);
    this.addEventListener("focusin", this.#handleFocusIn);
    this.addEventListener("focusout", this.#handleFocusOut);

    if (track) track.addEventListener("transitionend", this.#handleTransitionEnd);

    // ── Show first slide (no transition) ────────────────────────────────
    this.#jumpTo(0);
    this.#updateDots(0);
    this.#announce(0);

    // ── Autoplay ────────────────────────────────────────────────────────
    this.#startAutoplay();
  }

  #deactivate() {
    if (!this.#sliderActive) return;

    this.#sliderActive = false;
    this.removeAttribute("slider-active");
    this.#teardown();

    this.#removeClones();

    const track = this.#track;
    if (track) {
      track.style.transform = "";
      track.style.display = "";
      track.style.transition = "";
    }

    this.#allSlides.forEach((slide) => {
      slide.style.width = "";
      slide.style.flexShrink = "";
      slide.removeAttribute("role");
      slide.removeAttribute("aria-roledescription");
      slide.removeAttribute("aria-label");
      slide.removeAttribute("aria-hidden");
    });

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    const controls = this.#controls;
    if (controls) {
      controls.classList.remove("block");
      controls.classList.add("hidden");
      controls.setAttribute("aria-hidden", "true");
    }

    if (this.#liveRegion) this.#liveRegion.textContent = "";
  }

  #teardown() {
    this.#stopAutoplay();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    this.#prevBtn?.removeEventListener("click", this.#handlePrev);
    this.#nextBtn?.removeEventListener("click", this.#handleNext);

    this.#dots.forEach((dot) => {
      dot.removeEventListener("click", this.#handleDotClick);
    });

    this.removeEventListener("mouseenter", this.#handleMouseEnter);
    this.removeEventListener("mouseleave", this.#handleMouseLeave);
    this.removeEventListener("focusin", this.#handleFocusIn);
    this.removeEventListener("focusout", this.#handleFocusOut);

    const track = this.#track;
    if (track) track.removeEventListener("transitionend", this.#handleTransitionEnd);
  }

  // ─── Clone management ────────────────────────────────────────────────────

  #createClones() {
    const track = this.#track;
    if (!track || this.#realCount < 2) return;

    const realSlides = this.#allSlides;
    const first = realSlides[0];
    const last = realSlides[realSlides.length - 1];
    if (!first || !last) return;

    const firstClone = /** @type {HTMLElement} */ (first.cloneNode(true));
    const lastClone = /** @type {HTMLElement} */ (last.cloneNode(true));

    firstClone.setAttribute("data-clone", "");
    firstClone.setAttribute("aria-hidden", "true");
    firstClone.removeAttribute("role");

    lastClone.setAttribute("data-clone", "");
    lastClone.setAttribute("aria-hidden", "true");
    lastClone.removeAttribute("role");

    track.appendChild(firstClone);
    track.insertBefore(lastClone, track.firstChild);
  }

  #removeClones() {
    const track = this.#track;
    if (!track) return;
    track.querySelectorAll("[data-clone]").forEach((el) => el.remove());
  }

  // ─── Slide sizing ────────────────────────────────────────────────────────

  #sizeSlides() {
    const w = this.#slideWidth;
    this.#allSlides.forEach((slide) => {
      slide.style.width = `${w}px`;
    });
  }

  // ─── Track positioning ───────────────────────────────────────────────────

  /**
   * Track index = logical index + 1 (because of the prepended last-clone).
   * @param {number} logicalIndex
   */
  #trackIndex(logicalIndex) {
    return this.#realCount >= 2 ? logicalIndex + 1 : logicalIndex;
  }

  static #TRANSITION = "transform 700ms cubic-bezier(0.4, 0, 0.2, 1)";

  /** Instantly jump to a logical slide index (no transition).
   * @param {number} logicalIndex
   */
  #jumpTo(logicalIndex) {
    const track = this.#track;
    if (!track) return;
    const ti = this.#trackIndex(logicalIndex);
    const tx = -(ti * this.#slideWidth);
    track.style.transition = "none";
    track.style.transform = `translateX(${tx}px)`;
    track.offsetHeight;
    track.style.transition = CompactCardsSlider.#TRANSITION;
    this.#current = logicalIndex;
  }

  /** Animate to a logical slide index.
   * @param {number} logicalIndex
   */
  #slideTo(logicalIndex) {
    if (this.#transitioning) return;

    const track = this.#track;
    if (!track) return;

    const ti = this.#trackIndex(logicalIndex);
    const tx = -(ti * this.#slideWidth);

    if (this.#reducedMotion) {
      track.style.transition = "none";
      track.style.transform = `translateX(${tx}px)`;
      track.offsetHeight;
      track.style.transition = "none";
      this.#current = logicalIndex;
      this.#updateDots(logicalIndex);
      this.#announce(logicalIndex);
      return;
    }

    this.#transitioning = true;
    track.style.transition = CompactCardsSlider.#TRANSITION;
    track.style.transform = `translateX(${tx}px)`;

    this.#current = logicalIndex;
    this.#updateDots(((logicalIndex % this.#realCount) + this.#realCount) % this.#realCount);
    this.#announce(((logicalIndex % this.#realCount) + this.#realCount) % this.#realCount);
  }

  #handleTransitionEnd = () => {
    this.#transitioning = false;

    const real = this.#realCount;
    if (real < 2) return;

    if (this.#current >= real) {
      this.#jumpTo(0);
      this.#updateDots(0);
    } else if (this.#current < 0) {
      this.#jumpTo(real - 1);
      this.#updateDots(real - 1);
    }
  };

  // ─── Navigation ──────────────────────────────────────────────────────────

  #next() {
    if (!this.#realCount) return;
    if (this.#realCount < 2) return;

    const nextIndex = this.#current + 1;
    this.#slideTo(nextIndex);
  }

  #prev() {
    if (!this.#realCount) return;
    if (this.#realCount < 2) return;

    const prevIndex = this.#current - 1;
    this.#slideTo(prevIndex);
  }

  // ─── Dots ────────────────────────────────────────────────────────────────

  /** @param {number} activeIndex */
  #updateDots(activeIndex) {
    this.#dots.forEach((dot, i) => {
      const isActive = i === activeIndex;
      dot.setAttribute("aria-selected", String(isActive));
      if (isActive) {
        dot.classList.remove("bg-white/40");
        dot.classList.add("bg-white");
      } else {
        dot.classList.remove("bg-white");
        dot.classList.add("bg-white/40");
      }
    });
  }

  /** @param {Event} e */
  #handleDotClick = (e) => {
    const target = /** @type {HTMLElement} */ (e.currentTarget);
    const index = parseInt(target.dataset.dot ?? "0", 10);
    this.#stopAutoplay();
    this.#slideTo(index);
    this.#startAutoplay();
  };

  // ─── Autoplay ────────────────────────────────────────────────────────────

  #startAutoplay() {
    if (this.#reducedMotion) return;

    this.#stopAutoplay();
    this.#autoplayTimer = window.setInterval(() => {
      if (!this.#paused && !document.hidden) {
        this.#next();
      }
    }, this.#autoplaySeconds * 1000);
  }

  #stopAutoplay() {
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  }

  // ─── Pause / resume ──────────────────────────────────────────────────────

  #handleMouseEnter = () => {
    this.#paused = true;
  };
  #handleMouseLeave = () => {
    this.#paused = false;
  };
  #handleFocusIn = () => {
    this.#paused = true;
  };
  #handleFocusOut = () => {
    this.#paused = false;
  };

  // ─── Button handlers ─────────────────────────────────────────────────────

  #handlePrev = () => {
    this.#stopAutoplay();
    this.#prev();
    this.#startAutoplay();
  };

  #handleNext = () => {
    this.#stopAutoplay();
    this.#next();
    this.#startAutoplay();
  };

  // ─── Accessibility ────────────────────────────────────────────────────────

  /**
   * @param {number} n
   * @param {number} total
   */
  #slideLabel(n, total) {
    const tpl = Theme?.translations?.compact_cards_slide_of;
    if (tpl) return tpl.replace("{{ index }}", String(n)).replace("{{ length }}", String(total));
    return `${n} of ${total}`;
  }

  /** @param {number} index */
  #announce(index) {
    const live = this.#liveRegion;
    if (!live) return;

    const label = this.#slideLabel(index + 1, this.#realCount);
    live.textContent = "";
    requestAnimationFrame(() => {
      live.textContent = label;
    });
  }
}

if (!customElements.get("compact-cards-slider")) {
  customElements.define("compact-cards-slider", CompactCardsSlider);
}
